import { Tooltip } from '@mantine/core'
import { arrayOfObjectsSearch } from '@semios/app-platform-common'
import type { TRole } from 'App/Map/Admin/utils/useAdminEntites'
import { HierarchicalSelection } from 'components/HierarchicalSelection/HierarchicalSelection'
import { IconInfoLight } from 'components/icons/IconInfoLight'
import { translate } from 'i18n/i18n'
import React, { useEffect, useState } from 'react'
import { colors } from 'settings/colors'

const rowWrapperStyle = {
  alignItems: 'center',
  border: '1px solid rgb(233, 233, 233)',
  display: 'flex',
  padding: '4px 10px',
  transition: '0.15s ease all',
}

const selectedRowStyle = {
  background: colors.primary,
  border: `1px solid ${colors.primary}`,
  color: colors.white,
}

const regularRowStyle = {
  '&:hover': {
    background: colors.subtleBackground,
  },
}

const RoleRow = ({
  role,
  count,
  isSelected,
  setIsSelected,
  style,
}: {
  role: TRole
  count: number
  isSelected: boolean
  setIsSelected: (isSelected: boolean) => void
  style?: React.CSSProperties
}) => {
  const extraRowStyle = isSelected ? selectedRowStyle : regularRowStyle

  return (
    <div
      css={{
        ...rowWrapperStyle,
        ...extraRowStyle,
        ...style,
        cursor: 'pointer',
      }}
      onClick={() => setIsSelected(!isSelected)}
    >
      <div css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <Tooltip label={role.description} withArrow position={'right'}>
          <span css={{ cursor: 'help', paddingRight: '5px' }}>
            <IconInfoLight />
          </span>
        </Tooltip>
        {count ? (
          <span>
            <strong>{`(${count}) ${role.name}`}</strong>
          </span>
        ) : (
          role.name
        )}
      </div>
    </div>
  )
}

export const RolesPicker = ({
  roles: rolesArg,
  selectedRole,
  roleIdToCountOfEntities,
  setSelectedRole,
  width,
}: {
  roles: TRole[]
  width: string
  selectedRole: TRole['id'] | null
  roleIdToCountOfEntities: Record<string, number>
  setSelectedRole: (role: TRole['id'] | null) => void
}) => {
  const [searchString, setSearchString] = useState<string | undefined>()
  const [roles, setRoles] = useState<TRole[]>(rolesArg)

  useEffect(() => {
    const searchedRoles = arrayOfObjectsSearch(rolesArg, searchString ?? '', ['name'])

    setRoles(searchedRoles)
  }, [searchString])

  return (
    <HierarchicalSelection
      width={width}
      searchString={searchString ?? ''}
      setSearchString={setSearchString}
      title={translate.phrases.banyanApp('Roles')}
      placeholder={translate.phrases.banyanApp('Search for a role')}
      items={roles}
      renderItem={({ index, style, data }) => (
        <RoleRow
          role={data[index]}
          count={roleIdToCountOfEntities[data[index].id]}
          style={style}
          isSelected={data[index].id === selectedRole}
          setIsSelected={(isSelected) => setSelectedRole(isSelected ? data[index].id : null)}
        />
      )}
    />
  )
}
